import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { requireWorkspace } from "../lib/requireWorkspace";
import { useLibrary } from "../hooks/useQueries";
import { PageSkeleton } from "../components/PageSkeleton";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

export default function LibraryPage() {
  let workspace = null;
  let initErr = null;
  try {
    workspace = requireWorkspace();
  } catch (e) {
    initErr = e.message || "No workspace selected.";
  }

  const { data, isLoading, error } = useLibrary(workspace?.id);
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingOne, setLoadingOne] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState(null);

  const items = data?.items || [];

  async function openGeneration(id) {
    if (!workspace) return;
    setExportMsg(null);
    setSelectedId(id);
    setLoadingOne(true);
    try {
      const res = await api.get(`/api/generations/${id}`);
      setSelected(res.data);
    } catch (e) {
      setSelected(null);
    } finally {
      setLoadingOne(false);
    }
  }

  async function exportGeneration() {
    if (!selectedId) return;
    setExportMsg(null);
    setExporting(true);
    try {
      const res = await api.get(`/api/generations/${selectedId}/export`);
      const blob = new Blob([JSON.stringify(res.data.export, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `omnimark_export_${selectedId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setExportMsg("Export downloaded.");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) {
        setExportMsg(detail || "Export is locked on trial. Please upgrade to export.");
      } else {
        setExportMsg(detail || "Export failed. Try again.");
      }
    } finally {
      setExporting(false);
    }
  }

  if (!workspace) {
    return (
      <div className="space-y-4" data-testid="library-no-workspace">
        <div className="text-2xl font-semibold">Library</div>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="text-sm text-muted-foreground">{initErr || "No workspace selected."}</div>
            <div className="mt-3 flex gap-2">
              <Link to="/app/workspaces"><Button>Go to Workspaces</Button></Link>
              <Link to="/app/ads"><Button variant="outline">Generate Ad Pack</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) return <PageSkeleton />;

  const err = error?.response?.data?.detail || (error ? "Failed to load library." : null);

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-2xl font-semibold">Library</div>
          <div className="text-sm text-muted-foreground">
            View and manage your saved generations for this workspace.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{workspace.name}</Badge>
          <Link to="/app/workspaces" className="text-sm text-muted-foreground hover:underline">change</Link>
        </div>
      </div>

      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Generations
              <Button
                variant="outline"
                size="sm"
                onClick={() => queryClient.invalidateQueries({ queryKey: ["library", workspace.id] })}
                data-testid="refresh-btn"
              >
                Refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {items.length === 0 ? (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">No generations yet. Create your first Meta Ad Pack.</div>
                <Link to="/app/ads"><Button className="w-full">Generate Ad Pack</Button></Link>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((it) => {
                  const active = it.id === selectedId;
                  const date = new Date(it.created_at).toLocaleString();
                  return (
                    <button
                      key={it.id}
                      onClick={() => openGeneration(it.id)}
                      className={["w-full rounded-lg border p-3 text-left transition", active ? "bg-muted" : "hover:bg-muted/60"].join(" ")}
                      data-testid={`generation-item-${it.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium">{it.type}</div>
                        {active && <Badge variant="secondary">Open</Badge>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{date}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground">{it.id}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Details
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => selected && navigator.clipboard.writeText(JSON.stringify(selected.output_json, null, 2))} disabled={!selected} data-testid="copy-json-btn">
                  Copy JSON
                </Button>
                <Button size="sm" onClick={exportGeneration} disabled={!selectedId || exporting} data-testid="export-btn">
                  {exporting ? "Exporting…" : "Export"}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {exportMsg && (
              <div className={["rounded-lg border p-3 text-sm", exportMsg.toLowerCase().includes("locked") ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"].join(" ")} data-testid="export-message">
                <div className="flex items-center justify-between gap-3">
                  <div>{exportMsg}</div>
                  {exportMsg.toLowerCase().includes("locked") && (
                    <Link to="/app/upgrade"><Button size="sm">Upgrade</Button></Link>
                  )}
                </div>
              </div>
            )}
            {!selectedId && <div className="text-sm text-muted-foreground">Select a generation from the list to view details.</div>}
            {loadingOne && <div className="text-sm text-muted-foreground">Loading generation…</div>}
            {selected && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{selected.type}</Badge>
                </div>
                <Separator />
                <div className="rounded-lg border bg-background p-3">
                  <pre className="max-h-[520px] overflow-auto text-xs leading-relaxed">
                    {JSON.stringify(selected.output_json, null, 2)}
                  </pre>
                </div>
                <div className="flex items-center justify-between">
                  <Link to="/app/ads" className="text-sm text-muted-foreground hover:underline">Generate another ad pack</Link>
                  <Link to="/app/upgrade"><Button variant="outline">Upgrade / Buy Credits</Button></Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
